import React, { useEffect, useState } from 'react';
import moment from 'moment';
import cn from '@/utils/cn';
import { DownloadPdfIcon, ExportIcon } from '@/components/Icons';
import {
  useMonthlyStatements,
  TMonthlyStatementRow
} from '@/features/selectedAccount/hooks/useMonthlyStatements';
import CurrencyFormat from 'react-currency-format';
import DataTable, { SortDirectionEnum, TTableColumn } from '@/components/DataTable';
import Button from '@/components/Button';
import YearPickerInput from '@/components/YearPickerInput';
import MonthlyStatementTableRow from './MonthlyStatementTableRow';
import Toast from '@/components/Toast';

const STATEMENTS_COLUMN = 'Statements';

interface MonthlyStatementsProps {
  walletId: string;
  accountName: string;
}

const getStatementKey = (row: TMonthlyStatementRow) => `${row.year}-${row.month}`;

type TDownloadSummaryCounts = {
  total: number;
  succeeded: number;
  failed: number;
  permanentFailures: number;
};

enum DownloadStateEnum {
  Success = 'success',
  AllUnavailable = 'all-unavailable',
  AllFailed = 'all-failed',
  PartialUnavailable = 'partial-unavailable',
  PartialFailed = 'partial-failed',
  PartialMixed = 'partial-mixed'
}

const getDownloadState = ({
  succeeded,
  failed,
  permanentFailures
}: TDownloadSummaryCounts): DownloadStateEnum => {
  if (failed === 0) return DownloadStateEnum.Success;
  if (succeeded === 0 && permanentFailures === failed) return DownloadStateEnum.AllUnavailable;
  if (succeeded === 0 && permanentFailures === 0) return DownloadStateEnum.AllFailed;
  if (permanentFailures === failed) return DownloadStateEnum.PartialUnavailable;
  if (permanentFailures === 0) return DownloadStateEnum.PartialFailed;
  return DownloadStateEnum.PartialMixed;
};

const TOAST_VARIANT_BY_STATE: Record<DownloadStateEnum, 'success' | 'error' | 'info'> = {
  [DownloadStateEnum.Success]: 'success',
  [DownloadStateEnum.AllUnavailable]: 'error',
  [DownloadStateEnum.AllFailed]: 'error',
  [DownloadStateEnum.PartialUnavailable]: 'info',
  [DownloadStateEnum.PartialFailed]: 'info',
  [DownloadStateEnum.PartialMixed]: 'info'
};

const getSummaryMessage = (counts: TDownloadSummaryCounts) => {
  const { total, succeeded, permanentFailures, failed } = counts;
  const retryableFailures = failed - permanentFailures;
  const noun = `statement${total > 1 ? 's' : ''}`;
  const downloadedTitle = `Downloaded ${succeeded} of ${total} ${noun}`;

  switch (getDownloadState(counts)) {
    case DownloadStateEnum.Success:
      return {
        title: `Downloaded ${total} of ${total} ${noun}`,
        subtitle: 'All files downloaded successfully'
      };
    case DownloadStateEnum.AllUnavailable:
      return { title: `Could not download ${total} ${noun}`, subtitle: 'All not available' };
    case DownloadStateEnum.AllFailed:
      return {
        title: `Could not download ${total} ${noun}`,
        subtitle: 'All failed. Retry failed downloads.'
      };
    case DownloadStateEnum.PartialUnavailable:
      return { title: downloadedTitle, subtitle: `${permanentFailures} not available` };
    case DownloadStateEnum.PartialFailed:
      return {
        title: downloadedTitle,
        subtitle: `${retryableFailures} failed. Retry failed downloads.`
      };
    case DownloadStateEnum.PartialMixed:
      return {
        title: downloadedTitle,
        subtitle: `${permanentFailures} not available, ${retryableFailures} failed. Retry failed downloads.`
      };
  }
};

const MonthlyStatements: React.FC<MonthlyStatementsProps> = ({ walletId, accountName }) => {
  const {
    isLoading,
    isYearChanging,
    firstDate,
    lastDate,
    displayedBalances,
    selectedYear,
    handleYearChange,
    fetchPdfUrl
  } = useMonthlyStatements(walletId);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [downloadSummary, setDownloadSummary] = useState<{
    isOpen: boolean;
    succeeded: number;
    failed: number;
    total: number;
    permanentFailures: number;
  }>({ isOpen: false, succeeded: 0, failed: 0, total: 0, permanentFailures: 0 });

  // Clear selection whenever the underlying dataset changes (wallet or year filter).
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [walletId, selectedYear]);

  const handleDownload = async (
    year: number,
    month: number
  ): Promise<{ success: boolean; isRetryable?: boolean }> => {
    const { url, error } = await fetchPdfUrl(year, month);
    if (!url) {
      return { success: false, isRetryable: error?.isRetryable };
    }
    try {
      const link = document.createElement('a');
      link.href = url;
      const monthLabel = moment(`${year}-${month}`, 'YYYY-M').format('MMMM-YYYY').toLowerCase();
      link.setAttribute('download', `bank-statement-${accountName}-${monthLabel}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return { success: true };
    } catch {
      return { success: false, isRetryable: false };
    }
  };

  const handleToggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelectedKeys((prev) =>
      prev.size === displayedBalances.length
        ? new Set()
        : new Set(displayedBalances.map(getStatementKey))
    );
  };

  const handleDownloadSelected = async () => {
    const selectedRows = displayedBalances.filter((row) => selectedKeys.has(getStatementKey(row)));
    let succeeded = 0;
    let failed = 0;
    let permanentFailures = 0;

    for (const row of selectedRows) {
      const result = await handleDownload(row.year, row.month);
      if (result.success) {
        succeeded++;
      } else {
        failed++;
        if (result.isRetryable === false) {
          permanentFailures++;
        }
      }
    }

    setDownloadSummary({
      isOpen: true,
      succeeded,
      failed,
      total: selectedRows.length,
      permanentFailures
    });
  };

  const minDate = firstDate ?? moment().subtract(1, 'year').startOf('year').toDate();
  const maxDate = lastDate
    ? moment(lastDate).endOf('month').toDate()
    : moment().startOf('month').toDate();

  const renderBalance = (value: number) => (
    <CurrencyFormat
      value={Number(value).toFixed(2)}
      displayType="text"
      thousandSeparator
      prefix="$"
      decimalScale={2}
    />
  );

  const columns: TTableColumn<TMonthlyStatementRow>[] = [
    {
      key: STATEMENTS_COLUMN,
      header: STATEMENTS_COLUMN,
      canSort: true,
      sortAccessor: (row) => row.year * 12 + row.month,
      tdClassName: 'text-sm text-black',
      cell: (row) => moment(`${row.year}-${row.month}`, 'YYYY-M').format('MMMM YYYY')
    },
    {
      key: 'Opening Balance',
      header: 'Opening Balance',
      tdClassName: 'text-sm text-emerge-navy',
      cell: (row) => renderBalance(row.openingBalance)
    },
    {
      key: 'Closing Balance',
      header: 'Closing Balance',
      tdClassName: 'text-sm text-emerge-navy',
      cell: (row) => renderBalance(row.closingBalance)
    },
    {
      key: 'Action',
      header: 'Action',
      cell: (row) => {
        const label = moment(`${row.year}-${row.month}`, 'YYYY-M').format('MMMM YYYY');
        return row.isPdfLoading ? (
          <span className="text-xs text-black/20">Loading...</span>
        ) : (
          <button
            type="button"
            onClick={() => handleDownload(row.year, row.month)}
            className="flex items-center gap-1 text-xs font-medium text-emerge-navy"
            aria-label={`Download PDF for ${label}`}>
            <DownloadPdfIcon size="xs" />
            Download pdf
          </button>
        );
      }
    }
  ];

  const showToastSummary = downloadSummary.failed > 0 || downloadSummary.succeeded > 0;
  const downloadState = getDownloadState(downloadSummary);
  const toastVariant = TOAST_VARIANT_BY_STATE[downloadState];
  const isSuccess = downloadState === DownloadStateEnum.Success;
  const { title: summaryTitle, subtitle: summarySubtitle } = getSummaryMessage(downloadSummary);

  return (
    <div className="bg-white shadow-sm">
      <Toast
        isOpen={showToastSummary && downloadSummary.isOpen}
        onClose={() => setDownloadSummary({ ...downloadSummary, isOpen: false })}
        variant={toastVariant}
        size="md">
        <div className="flex flex-col gap-y-1">
          <div
            className={cn(
              'text-sm font-medium',
              isSuccess ? 'text-emerge-navy' : 'text-error-darkred'
            )}>
            {summaryTitle}
          </div>
          <div
            className={cn(
              'text-xs font-normal',
              isSuccess ? 'text-emerge-navy-2' : 'text-error-darkred'
            )}>
            {summarySubtitle}
          </div>
        </div>
      </Toast>
      <div className="bg-white flex items-center justify-between gap-x-3 mb-3">
        <YearPickerInput
          selectedYear={selectedYear}
          onSelectYear={handleYearChange}
          minDate={minDate}
          maxDate={maxDate}
        />
        <Button
          variant="primary"
          disabled={selectedKeys.size === 0}
          onClick={handleDownloadSelected}>
          <ExportIcon size="xs" />
          Download selected
        </Button>
      </div>

      <div className="bg-white">
        <DataTable<TMonthlyStatementRow>
          columns={columns}
          data={displayedBalances}
          getRowKey={getStatementKey}
          isLoading={isLoading || isYearChanging}
          itemLabel="Statements"
          emptyMessage="No statements available for the selected period."
          pagination={{ mode: 'none' }}
          sorting={{
            mode: 'client',
            initialSort: { key: STATEMENTS_COLUMN, direction: SortDirectionEnum.DESC }
          }}
          selection={{
            selectedKeys,
            onToggleRow: (row) => handleToggleRow(getStatementKey(row)),
            onToggleAll: handleToggleAll
          }}
          renderRow={(row, index) => {
            const key = getStatementKey(row);
            return (
              <MonthlyStatementTableRow
                key={key}
                index={index}
                row={row}
                isPdfLoading={row.isPdfLoading}
                onDownload={handleDownload}
                isSelected={selectedKeys.has(key)}
                onToggleSelect={() => handleToggleRow(key)}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

export default MonthlyStatements;
